import type { DoczFragment, DoczHandle } from "@norkron/web-docz";
import { DoczEditBridge, type EditableBlock, type EditBridge } from "@norkron/web-page-layout";

/**
 * THE CANVAS UNDO SEAM — formatting state that rides the undo snapshot.
 *
 * `EditorCore`'s undo ring (web-docz's shared `SnapshotHistory`) snapshots exactly
 * ONE thing: `{bridge, selection}`. Everything the surface's paint depends on that
 * is NOT reachable from the bridge is, by construction, un-undoable — it survives
 * `undo()` untouched.
 *
 * That is precisely why the canvas had no undo for formatting. The mark model lived
 * in an app-level React ref (`CanvasElaborateEditor`'s `overridesRef`, a plain
 * mutable `Map`) which the snapshot could not see, and the commit path called
 * `EditorCore.refresh()`, which deliberately pushes NO snapshot at all. So after
 * three applied ribbon commands `canUndo()` answered `false` and the ribbon's undo
 * button did nothing — measured live.
 *
 * The fix has two rungs, both of which end at `EditorCore.applyBridge` (the ONE
 * undoable install seam) instead of `refresh()`:
 *
 *  1. **Fold the marks into the docz IR.** {@link foldFragmentIntoHandle} routes the
 *     verb's fragment through `DoczHandle.withBlockRuns`, so the marks stop being a
 *     side-car at all: they become part of the document the snapshot already holds.
 *     Guarded by {@link blockShapeStable} — `withBlockRuns` PROMOTES a legacy handle
 *     to a structured one, and that promotion can re-shape the legacy block list
 *     (an opaque `html` block explodes; a `table` re-flattens to a `paragraph` of
 *     cell text). Every canvas coordinate — the caret, the selection, every stored
 *     comment anchor — is a `blockOrd` into that list, so a re-shape would silently
 *     re-point all of them. Fold only when the shape is provably unchanged.
 *
 *  2. **Carry the residue on the bridge.** {@link FormatBridge} is a `DoczEditBridge`
 *     that additionally holds an IMMUTABLE override map. Because it IS the bridge,
 *     the existing snapshot mechanism restores it for free, with no change to
 *     `EditorCore` and no second history ring. A text edit re-wraps into a new
 *     `FormatBridge` carrying the same overrides, so the marks survive typing the
 *     same way the handle does.
 *
 * The relayout encoder reads the overrides OFF THE BRIDGE it is handed
 * ({@link overridesFor}), so an undo repaints from the restored state in the SAME
 * pass — never one frame late.
 */

/** The per-block styled-run overrides a bridge carries. Immutable by contract. */
export type OverrideMap = ReadonlyMap<string, DoczFragment>;

const NO_OVERRIDES: OverrideMap = new Map<string, DoczFragment>();

/**
 * A {@link DoczEditBridge} that also carries the session's per-block styled-run
 * overrides, so they are part of the state `EditorCore` snapshots for undo.
 *
 * Every mutation re-wraps into a new `FormatBridge` with the SAME overrides — a
 * plain `DoczEditBridge` would be produced otherwise and every mark on the page
 * would vanish on the next keystroke.
 *
 * ## It re-carries them through ONE seam, not through each verb (P0, 2026-07-30)
 *
 * This class used to override `editText`, `split` and `merge` individually and
 * re-wrap each result. That held only for as long as the base class had exactly
 * those three verbs. When `@norkron/web-page-layout` grew a FOURTH — `deleteBlock`,
 * the verb that finally lets an image / rule / table be removed by the keyboard —
 * this subclass would have silently degraded to a plain `DoczEditBridge` the
 * instant a reader deleted a block, taking every mark on the page with it.
 *
 * The base class now exposes `withHandle` as the one place a produced handle
 * becomes a bridge (upstream, so every consumer inherits the fix), and this
 * overrides that. A fifth verb cannot reintroduce the defect.
 */
export class FormatBridge extends DoczEditBridge {
  readonly overrides: OverrideMap;

  constructor(handle: DoczHandle, overrides: OverrideMap = NO_OVERRIDES) {
    super(handle);
    this.overrides = overrides;
  }

  /** THE seam: every verb the base class has — and every verb it ever gains —
   *  produces its new bridge through here, so the overrides ride along by
   *  construction. Identity for an honest no-op is the base class's business and
   *  is unaffected: `withHandle` is only reached when the handle really changed. */
  override withHandle(next: DoczHandle): EditBridge {
    return new FormatBridge(next, this.overrides);
  }

  /** The same document with a DIFFERENT override map (a format commit). */
  withOverrides(overrides: OverrideMap): FormatBridge {
    return new FormatBridge(this.handle, overrides);
  }
}

/** The override map a bridge carries, or `null` when it carries none (a plain
 *  `DoczEditBridge` / `PlainTextEditBridge` — e.g. one produced by the paste path
 *  or by a test harness). */
export function overridesOf(bridge: EditBridge | null | undefined): OverrideMap | null {
  return bridge instanceof FormatBridge ? bridge.overrides : null;
}

/** The overrides the relayout encoder must paint from: the bridge's own snapshot
 *  when it has one, else the caller's live map (back-compat for a bridge this app
 *  did not mint). Bridge-first is what makes an undo repaint correct in the same
 *  pass — the restored bridge carries the restored marks. */
export function overridesFor(
  bridge: EditBridge | null | undefined,
  live: OverrideMap,
): OverrideMap {
  return overridesOf(bridge) ?? live;
}

/** Make the host's LIVE (mutable, read-side) override map equal the bridge's
 *  snapshot. Called after every state change so a verb reading the live map sees
 *  what an undo just restored. No-op for a bridge that carries no overrides. */
export function syncOverrides(
  live: Map<string, DoczFragment>,
  bridge: EditBridge | null | undefined,
): void {
  const snap = overridesOf(bridge);
  if (!snap) return;
  live.clear();
  for (const [id, frag] of snap) live.set(id, frag);
}

/**
 * Is `after` the SAME block list as `before` apart from the block `exceptId`?
 *
 * The canvas addresses everything by `blockOrd` — an index into this list. A fold
 * that changes the list's length, its ids, or any OTHER block's kind/text has
 * silently re-pointed the caret, the live selection and every comment anchor at
 * different content. `withBlockRuns` can do exactly that when it promotes a legacy
 * handle (raw-HTML explosion, table→paragraph flattening), so this is the gate the
 * fold has to pass.
 *
 * The excepted block itself may change freely — a `changeCase` verb rewrites its
 * text and a block-kind conversion rewrites its kind; both are the point of the
 * verb.
 */
export function blockShapeStable(
  before: readonly EditableBlock[],
  after: readonly EditableBlock[],
  exceptId: string,
): boolean {
  if (before.length !== after.length) return false;
  for (let i = 0; i < before.length; i++) {
    const a = before[i];
    const b = after[i];
    if (!a || !b) return false;
    if (a.id !== b.id) return false;
    if (a.id === exceptId) continue;
    if (a.text !== b.text) return false;
    if ((a.kind ?? "paragraph") !== (b.kind ?? "paragraph")) return false;
    if ((a.level ?? 0) !== (b.level ?? 0)) return false;
  }
  return true;
}

/**
 * Fold a verb's styled fragment into the docz IR through the REAL
 * `DoczHandle.withBlockRuns` verb, returning the new handle — or `null` when the
 * fold is not safe, in which case the caller keeps the fragment as a session
 * override on the {@link FormatBridge} (still undoable, just not yet in the IR).
 *
 * Declines in three cases, each of which would otherwise LOSE something:
 *
 *  1. **The fragment carries a `para` side-channel** (a Bundle-3 drop cap set by
 *     `setParaProps`). `web-docz`'s `fragmentIntoBlock` preserves the BLOCK's own
 *     `props` and ignores `frag.para`, so folding would silently discard the drop
 *     cap. Those fragments stay on the bridge, where the structured encoder's
 *     `frag.para ?? sb.props` precedence still paints them.
 *  2. **The handle declined** (`withBlockRuns` returns the same handle for an
 *     unknown block).
 *  3. **The promotion re-shaped the block list** — see {@link blockShapeStable}.
 */
export function foldFragmentIntoHandle(
  handle: DoczHandle,
  blockId: string,
  frag: DoczFragment,
): DoczHandle | null {
  if ((frag as { para?: unknown }).para !== undefined) return null;
  const next = handle.withBlockRuns(blockId, frag);
  if (next === handle) return null;
  if (!blockShapeStable(handle.blocks, next.blocks, blockId)) return null;
  return next;
}
